import { useEffect, useState } from 'react';
import { AccountLayout } from '@/components/AccountLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import { requireUser } from '@/lib/requireUser';
import { Gift } from 'lucide-react';
import { toast } from 'sonner';

const Credits = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [giftCode, setGiftCode] = useState('');

  useEffect(() => {
    requireUser({ user, navigate, currentPath: '/account/credits' });
  }, [user, navigate]);

  const handleRedeem = () => {
    if (!giftCode.trim()) {
      toast.error('Please enter a gift card code');
      return;
    }
    toast.info('Gift card redemption coming soon!');
    setGiftCode('');
  };

  if (!user) return null;

  return (
    <AccountLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Credits & Gift Cards</h1>
          <p className="text-muted-foreground mt-2">Manage your account credits and redeem gift cards</p>
        </div>

        {/* Credit Balance */}
        <Card>
          <CardHeader>
            <CardTitle>Current Balance</CardTitle>
            <CardDescription>Your available credit balance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <p className="text-5xl font-bold text-primary">$0.00</p>
                <p className="text-sm text-muted-foreground mt-2">Available Credits</p>
              </div>
            </div>
            <Button className="w-full mt-4" disabled>
              Purchase Credits
            </Button>
          </CardContent>
        </Card>

        {/* Redeem Gift Card */}
        <Card>
          <CardHeader>
            <CardTitle>Redeem Gift Card</CardTitle>
            <CardDescription>Enter your gift card code to add credits</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gift-code">Gift Card Code</Label>
              <div className="flex gap-2">
                <Input 
                  id="gift-code" 
                  placeholder="XXXX-XXXX-XXXX-XXXX" 
                  value={giftCode}
                  onChange={(e) => setGiftCode(e.target.value)}
                />
                <Button onClick={handleRedeem}>
                  <Gift className="h-4 w-4 mr-2" />
                  Redeem
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credits History */}
        <Card>
          <CardHeader>
            <CardTitle>Credits History</CardTitle>
            <CardDescription>Your credit transactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <p className="text-sm text-muted-foreground">No transactions yet</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AccountLayout>
  );
};

export default Credits;
